import { createConnectTransport } from '@connectrpc/connect-web'
import type { Interceptor } from '@connectrpc/connect'
import { Code } from '@connectrpc/connect'
import type {
  Application,
  ApplicationHealthSummary,
  ApplicationResourceStatus,
  AuditLogEntry,
  ContainerStatus,
  ControllerHealth,
  NotificationSettings,
  OperatorMetrics,
  PodStatus,
  Project,
  ProjectMember,
  ResourceEvent,
  ServiceHealth,
  Settings,
  TrafficDataPoint,
  TrafficMetrics,
} from '@/lib/generated-types'
import type { GetSystemHealthResponse as SystemHealth } from '@/gen/catpocs/v1/admin_pb'

// Interceptor to handle 401 Unauthorized responses
const authInterceptor: Interceptor = (next) => async (req) => {
  try {
    return await next(req)
  } catch (error: unknown) {
    // Check if the error is a Connect error with Unauthenticated status (401)
    // ConnectError has a 'code' property that matches Code enum
    const errorCode = typeof error === 'object' && error !== null && 'code' in error
      ? (error as { code?: unknown }).code
      : undefined

    if (errorCode === Code.Unauthenticated || errorCode === 'unauthenticated') {
      console.log('Unauthenticated error detected (401), redirecting to login...')
      // Redirect to SSO login immediately
      window.location.href = '/auth/login'
      // Don't throw - we're redirecting anyway
      return Promise.reject(error)
    }
    throw error
  }
}

// Import generated Connect-Query hooks
export {
  // Projects
  createProject,
  getProject,
  listProjects,
  updateProject,
  deleteProject,
  addProjectMember,
  removeProjectMember,
  listProjectMembers,
} from '@/gen/catpocs/v1/projects-ProjectService_connectquery'

export {
  // Applications
  createApplication,
  getApplication,
  listApplications,
  deleteApplication,
  syncApplication,
  updateApplicationSyncPolicy,
  suspendApplication,
  resumeApplication,
  retryCreateCRD,
  retryHelmValidation,
  retryApplicationSetup,
  startApplicationValidation,
  forceDeleteApplication,
  upsertApplicationGitHubVariables,
  getApplicationEvents,
  getApplicationDiff,
  getDeploymentHistory,
  getApplicationTrafficMetrics,
  getApplicationResourceStatus,
  listApplicationsWithIssues,
  listApplicationSetupAttempts,
} from '@/gen/catpocs/v1/applications-ApplicationService_connectquery'

export {
  // Admin
  listAllProjects,
  getAuditLogs,
  getOperatorMetrics,
  validateRepository,
  getSystemHealth,
  listActiveSessions,
  revokeSession,
  revokeUserSessions,
  // Users
  listUsers,
  getUserStatistics,
  // Impersonation
  startImpersonation,
  stopImpersonation,
} from '@/gen/catpocs/v1/admin-AdminService_connectquery'

export {
  // Roles
  getCurrentUser,
  getUserPermissions,
  listSuperAdmins,
  addSuperAdmin,
  removeSuperAdmin,
} from '@/gen/catpocs/v1/roles-RoleService_connectquery'

export {
  // Deployments
  listImageTags,
  getImageDeployments,
  deployImageTag as deployImageTagRPC,
  getDeploymentStatus,
  configureTagTracking,
} from '@/gen/catpocs/v1/deployments-DeploymentService_connectquery'

export {
  // ECR Admin
  getRegistryOverview,
  listRepositories as listECRRepositories,
  getRepositoryDetails,
  getVulnerableImages,
  getUnusedImages,
  cleanupUnusedImages,
} from '@/gen/catpocs/v1/ecr_admin-ECRAdminService_connectquery'

export {
  // Settings
  getSettings,
  updateSettings,
  testNotifications,
} from '@/gen/catpocs/v1/settings-SettingsService_connectquery'

// API client configuration
export const transport = createConnectTransport({
  baseUrl: import.meta.env.DEV ? 'http://localhost:8080' : window.location.origin,
  interceptors: [authInterceptor],
})

// Helper to get the API base URL
export function getApiBaseUrl(): string {
  return import.meta.env.DEV ? 'http://localhost:8080' : window.location.origin
}

// Canonical API contract types come from generated protobuf messages.
export type {
  ProjectMember,
  Project,
  Application,
  OperatorMetrics,
  TrafficDataPoint,
  TrafficMetrics,
  ContainerStatus,
  PodStatus,
  ResourceEvent,
  ApplicationResourceStatus,
  AuditLogEntry,
  ServiceHealth,
  ControllerHealth,
  ApplicationHealthSummary,
  NotificationSettings,
  Settings,
  SystemHealth,
}

// ============================================================================
// Streaming RPC support (not available via Connect-Query hooks)
// Connect-Query only generates hooks for unary RPCs.
// For streaming RPCs, we use the Connect client directly.
// ============================================================================

import { createClient } from '@connectrpc/connect'
import { ApplicationService, GetApplicationLogsResponse, StreamApplicationStatusResponse } from '@/gen/catpocs/v1/applications_pb'

// Create a typed client for streaming operations
const applicationClient = createClient(ApplicationService, transport)

export interface ApplicationLogStreamOptions {
  podName?: string
  containerName?: string
  tailLines?: number
  follow?: boolean
  signal?: AbortSignal
}

export type ApplicationLogEntry = GetApplicationLogsResponse

export type ApplicationStatusStreamEntry = StreamApplicationStatusResponse

export async function* streamApplicationLogs(
  applicationId: string,
  options?: ApplicationLogStreamOptions
): AsyncGenerator<ApplicationLogEntry> {
  const stream = applicationClient.getApplicationLogs({
    id: applicationId,
    podName: options?.podName ?? '',
    containerName: options?.containerName ?? '',
    tailLines: BigInt(options?.tailLines ?? 100),
    follow: options?.follow ?? false,
  }, options?.signal ? { signal: options.signal } : undefined)

  for await (const response of stream) {
    yield response
  }
}// GetApplicationLogs collects a snapshot of log lines
export async function getApplicationLogs(
  applicationId: string,
  options?: ApplicationLogStreamOptions
): Promise<string[]> {
  const logs: string[] = []
  for await (const response of streamApplicationLogs(applicationId, options)) {
    logs.push(response.logLine)
  }
  return logs
}

export async function* streamApplicationStatus(
  applicationId: string,
  options?: { signal?: AbortSignal }
): AsyncGenerator<ApplicationStatusStreamEntry> {
  const stream = applicationClient.streamApplicationStatus(
    { applicationId },
    options?.signal ? { signal: options.signal } : undefined
  )

  for await (const response of stream) {
    yield response
  }
}
